import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchPaymentConfig, createBookOrder, captureBookOrder, createBundleOrder, captureBundleOrder } from "../../lib/api";

// PayPal checkout for a book (or the bundle) digital download. Calls onPurchased(downloadToken) after capture.
export default function BookPayButton({ bookId, bundle = false, onPurchased }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentConfig()
      .then(setConfig)
      .catch(() => setConfig({ configured: false }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-3" data-testid="book-paypal-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading secure payment…
      </div>
    );
  }

  if (!config?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex gap-3" data-testid="book-paypal-not-configured">
        <AlertTriangle className="h-5 w-5 flex-shrink-0" />
        <div>Online payments aren't enabled yet. Please contact our office at <span className="font-medium">(980) 294-7187</span>.</div>
      </div>
    );
  }

  return (
    <div data-testid="book-paypal-button-wrap">
      <PayPalScriptProvider options={{ "client-id": config.client_id, currency: config.currency || "USD" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "buynow" }}
          createOrder={async () => {
            try {
              const res = bundle ? await createBundleOrder() : await createBookOrder(bookId);
              return res.order_id;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not start the payment.");
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = bundle
                ? await captureBundleOrder(data.orderID)
                : await captureBookOrder(bookId, data.orderID);
              toast.success("Payment successful! Your download is ready.");
              onPurchased?.(res.download_token);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment could not be completed.");
            }
          }}
          onError={() => toast.error("Something went wrong with PayPal. Please try again.")}
        />
      </PayPalScriptProvider>
    </div>
  );
}
